import React from 'react';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Trophy, Target, TrendingUp } from 'lucide-react';
import { Match, Team } from '@/types/cricket';
import Confetti from 'react-confetti';
import useSound from 'use-sound';
import victorySound from '@/assets/victory.mp3';

interface MatchStatusProps {
  match: Match;
  teams: Team[];
}

export const MatchStatus: React.FC<MatchStatusProps> = ({ match, teams }) => {
  const team1 = teams.find(t => t.id === match.team1Id);
  const team2 = teams.find(t => t.id === match.team2Id);
  
  const battingTeam = teams.find(t => t.id === match.currentBattingTeam);
  const bowlingTeam = teams.find(t => t.id !== match.currentBattingTeam);
  
  const currentScore = match.currentBattingTeam === match.team1Id ? match.team1Score : match.team2Score;
  const previousScore = match.currentBattingTeam === match.team1Id ? match.team2Score : match.team1Score;

  // Calculate target and lead information
  const calculateMatchInfo = () => {
    if (match.status === 'completed') {
      const team1Total = match.team1Score.runs;
      const team2Total = match.team2Score.runs;
      const winner = team1Total > team2Total ? team1 : team2;
      const margin = Math.abs(team1Total - team2Total);
      
      let marginDescription = '';
      if (winner) {
        if (team1Total !== team2Total) { // Only calculate margin if there's a difference
          if (winner.id === match.team1Id) {
            marginDescription = `${margin} run${margin !== 1 ? 's' : ''}`;
          } else {
            // For wickets, calculate how many wickets are left
            const wicketsRemaining = (match.wicketsPerInnings || 10) - (winner.id === match.team1Id ? match.team2Score.wickets : match.team1Score.wickets);
            marginDescription = `${wicketsRemaining} wicket${wicketsRemaining !== 1 ? 's' : ''}`;
          }
        }
      }

      // Determine victory type
      if (match.currentInnings === 1) {
        return {
          type: 'completed_first_innings',
          winner: null,
          margin: 0,
          message: `${battingTeam?.name} completed their innings`,
          marginDescription: ''
        };
      } else {
        return {
          type: 'match_completed',
          winner,
          margin,
          message: `${winner?.name} won by ${margin} runs`,
          marginDescription
        };
      }
    }
    
    if (match.currentInnings === 2) {
      const target = previousScore.runs + 1;
      const needed = target - currentScore.runs;
      const ballsRemaining = ((match.totalOvers || 20) * (match.ballsPerOver || 6)) - 
                           (currentScore.overs * (match.ballsPerOver || 6) + currentScore.balls);
      
      if (needed <= 0) {
        return {
          type: 'target_achieved',
          target,
          needed: 0,
          ballsRemaining,
          message: `${battingTeam?.name} won by ${10 - currentScore.wickets} wickets`,
          marginDescription: ''
        };
      }
      
      return {
        type: 'chasing',
        target,
        needed,
        ballsRemaining,
        message: `${battingTeam?.name} needs ${needed} runs from ${Math.ceil(ballsRemaining / 6)} overs`,
        marginDescription: ''
      };
    }
    
    return {
      type: 'first_innings',
      message: `${battingTeam?.name} batting first`,
      marginDescription: ''
    };
  };

  const matchInfo = calculateMatchInfo();

  // Confetti and sound effect logic
  const [showConfetti, setShowConfetti] = React.useState(false);
  const [confettiPieces, setConfettiPieces] = React.useState(400);
  const [playVictory] = useSound(victorySound, { volume: 0.7 });
  React.useEffect(() => {
    if (matchInfo.type === 'match_completed' || matchInfo.type === 'target_achieved') {
      setShowConfetti(true);
      setConfettiPieces(400);
      playVictory();
      const timer1 = setTimeout(() => setConfettiPieces(0), 5000); // Stop generating new pieces after 5s
      const timer2 = setTimeout(() => setShowConfetti(false), 8000); // Unmount after pieces have fallen
      return () => { clearTimeout(timer1); clearTimeout(timer2); };
    }
  }, [matchInfo.type, playVictory]);

  const getStatusColor = () => {
    switch (matchInfo.type) {
      case 'match_completed':
      case 'target_achieved':
        return 'bg-green-500';
      case 'chasing':
        return 'bg-blue-500';
      default:
        return 'bg-gray-500';
    }
  };

  return (
    <div className="space-y-4">
      {showConfetti && <Confetti width={window.innerWidth} height={window.innerHeight} numberOfPieces={confettiPieces} recycle={false} />}
      {/* Current Match Status */}
      <Card className="border-2 border-cricket-green/20">
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              {matchInfo.type === 'match_completed' || matchInfo.type === 'target_achieved' ? (
                <Trophy className="w-5 h-5 text-yellow-500 animate-bounce" />
              ) : matchInfo.type === 'chasing' ? (
                <Target className="w-5 h-5 text-blue-500" />
              ) : (
                <TrendingUp className="w-5 h-5 text-green-500" />
              )}
              <span className="font-semibold">{matchInfo.message}</span>
            </div>
            <Badge className={`${getStatusColor()} text-white animate-pulse`}>
              {match.status === 'completed' ? 'FINISHED' : 'LIVE'}
            </Badge>
          </div>
        </CardContent>
      </Card>

      {/* Target Information for Second Innings */}
      {match.currentInnings === 2 && matchInfo.type === 'chasing' && (
        <Card className="border-blue-200 bg-muted animate-fade-in">
          <CardContent className="p-4">
            <div className="grid grid-cols-3 gap-4 text-center">
              <div>
                <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">{matchInfo.target}</div>
                <div className="text-sm text-muted-foreground">Target</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-red-600 dark:text-red-400">{matchInfo.needed}</div>
                <div className="text-sm text-muted-foreground">Needed</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-green-600 dark:text-green-400">
                  {Math.floor(matchInfo.ballsRemaining / (match.ballsPerOver || 6))}.{matchInfo.ballsRemaining % (match.ballsPerOver || 6)}
                </div>
                <div className="text-sm text-muted-foreground">Overs Left</div>
              </div>
            </div>
            
            {/* Run Rate Information */}
            <div className="mt-4 pt-4 border-t grid grid-cols-2 gap-4 text-center">
              <div>
                <div className="text-lg font-semibold text-foreground">
                  {currentScore.overs > 0 ? (currentScore.runs / (currentScore.overs + currentScore.balls / (match.ballsPerOver || 6))).toFixed(2) : '0.00'}
                </div>
                <div className="text-xs text-muted-foreground">Current RR</div>
              </div>
              <div>
                <div className="text-lg font-semibold text-foreground">
                  {matchInfo.ballsRemaining > 0 ? (matchInfo.needed / (matchInfo.ballsRemaining / (match.ballsPerOver || 6))).toFixed(2) : '0.00'}
                </div>
                <div className="text-xs text-muted-foreground">Required RR</div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Previous Team Score (Show in second innings) */}
      {match.currentInnings === 2 && (
        <Card className="border-gray-200 dark:border-border bg-gray-50 dark:bg-gray-900">
          <CardContent className="p-4">
            <div className="flex justify-between items-center">
              <span className="font-medium text-foreground">
                {bowlingTeam?.name} (1st Innings)
              </span>
              <span className="text-lg font-bold text-foreground">
                {previousScore.runs}/{previousScore.wickets} ({previousScore.overs}.{previousScore.balls})
              </span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Victory Animation */}
      {(matchInfo.type === 'match_completed' || matchInfo.type === 'target_achieved') && (
        <Card className="border-yellow-300 dark:border-yellow-600 bg-card dark:bg-zinc-900 animate-scale-in">
          <CardContent className="p-6 text-center">
            <Trophy className="w-12 h-12 text-yellow-500 dark:text-yellow-300 mx-auto mb-4 animate-bounce" />
            <h2 className="text-2xl font-bold text-foreground mb-2">
              🎉 {matchInfo.winner?.name} Wins! 🎉
            </h2>
            <p className="text-center text-sm mb-4 text-foreground">Won by {matchInfo.marginDescription}</p>
            <div className="grid grid-cols-2 gap-4">
              <div className={`p-4 rounded-lg ${matchInfo.winner?.id === team1?.id ? 'bg-green-100 dark:bg-green-700 !important border-2 border-green-300 dark:border-green-600' : 'bg-gray-100 dark:bg-gray-700 !important'}`}>
                <p className="font-bold text-lg text-foreground">{team1?.name}</p>
                <p className="text-sm text-muted-foreground">{match.team1Score.runs}/{match.team1Score.wickets}</p>
                <p className="text-sm text-muted-foreground">({match.team1Score.overs}.{match.team1Score.balls} overs)</p>
              </div>
              <div className={`p-4 rounded-lg ${matchInfo.winner?.id === team2?.id ? 'bg-green-100 dark:bg-green-700 !important border-2 border-green-300 dark:border-green-600' : 'bg-gray-100 dark:bg-gray-700 !important'}`}>
                <p className="font-bold text-lg text-foreground">{team2?.name}</p>
                <p className="text-sm text-muted-foreground">{match.team2Score.runs}/{match.team2Score.wickets}</p>
                <p className="text-sm text-muted-foreground">({match.team2Score.overs}.{match.team2Score.balls} overs)</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};
